'use client';
import { CategoryChipSkeleton } from '@/components';
import type { Tag } from '@/types';
import { CategoryChip } from './CategoryChip';

type CategoryChipListProps = {
  selectedSlug: string;
  tags: Tag[];
  isLoading: boolean;
  onTagClick: (slug: string) => void;
  activeCategorySlug: string;
};

function CategoryChipItem({
  category,
  isActive,
  onTagClick,
  tag_slug,
}: {
  category: string;
  isActive: boolean;
  onTagClick: (slug: string) => void;
  tag_slug: string;
}) {
  const handleClick = () => {
    if (onTagClick) {
      onTagClick(tag_slug ?? '');
    }
  };

  return (
    <CategoryChip label={category} isActive={isActive} onClick={handleClick} tag_slug={tag_slug} />
  );
}

export function CategoryChipList({
  selectedSlug,
  tags,
  isLoading = false,
  onTagClick,
  activeCategorySlug,
}: CategoryChipListProps) {
  if (isLoading) {
    return <CategoryChipSkeleton />;
  }

  if (!tags) {
    return null;
  }

  const finalTags = tags || [];

  const chipsToRender = [];

  if (!isLoading && onTagClick) {
    // 'All' is active if no tag selected OR it matches the active category slug
    const isAllActive = !selectedSlug || selectedSlug === '' || selectedSlug === activeCategorySlug;
    chipsToRender.push(
      <CategoryChipItem
        key="all-chip"
        category="All"
        isActive={isAllActive}
        onTagClick={onTagClick}
        tag_slug={activeCategorySlug ?? 'all'}
      />
    );
  }

  const renderCategoryChip = (item: Tag) => {
    const isActive = item?.slug === (selectedSlug || '');

    return (
      <CategoryChipItem
        key={item.label}
        category={item.label}
        isActive={isActive}
        onTagClick={onTagClick}
        tag_slug={item?.slug}
      />
    );
  };

  chipsToRender.push(...finalTags.map(renderCategoryChip));

  return <>{chipsToRender}</>;
}

export default CategoryChipList;
